import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SPINOFF_AUTH_CONFIG } from '../auth/spinoff-auth.config';
import { SPINOFF_LOCAL_ERROR_HANDLING } from '../auth/spinoff-auth-error-handling.config';
import { SPINOFF_TWO_FACTOR_AUTH_PROMPT } from './spinoff-two-factor-auth.prompt';
import {
  SpinoffTwoFactorAuthCancelledError,
  type SpinoffTwoFactorAuthChallenge,
  type SpinoffTwoFactorAuthMethod,
  type SpinoffTwoFactorAuthStatus,
  type SpinoffTwoFactorAuthVerified,
} from './spinoff-two-factor-auth.type';

/**
 * 투팩터 인증 API 호출과 검증 프롬프트 조율. 모든 호출은 `SPINOFF_LOCAL_ERROR_HANDLING`을 켠다 — 사용자가 이미 화면에서 직접 트리거한
 * 액션이라, 인증 문자 발송 실패(502)가 앱 전체를 오류 화면으로 덮어 입력하던 내용이 사라지지 않게 토스트로만 알린다.
 */
@Injectable({ providedIn: 'root' })
export class SpinoffTwoFactorAuthService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(SPINOFF_AUTH_CONFIG);
  private readonly prompt = inject(SPINOFF_TWO_FACTOR_AUTH_PROMPT);

  /** 진행 중인 프롬프트 — 428이 동시에 여러 건 와도 다이얼로그는 하나만 열고 그 결과를 공유한다. */
  private inflight: Promise<void> | null = null;

  /** 현재 세션의 투팩터 인증 유효 여부. 보호 API를 부르기 전에 미리 안내하려는 선택적 용도다. */
  status(): Promise<SpinoffTwoFactorAuthStatus> {
    return firstValueFrom(this.http.get<SpinoffTwoFactorAuthStatus>(this.url('/two-factor-auth/status'), { context: localErrorContext() }));
  }

  /** 계정에 묶인 검증된 번호로 인증 코드를 보낸다. 번호는 요청으로 보내지 않는다(서버가 계정에서 결정). */
  startChallenge(method: SpinoffTwoFactorAuthMethod = 'otp'): Promise<SpinoffTwoFactorAuthChallenge> {
    return firstValueFrom(
      this.http.post<SpinoffTwoFactorAuthChallenge>(this.url('/two-factor-auth/challenges'), { method }, { context: localErrorContext() }),
    );
  }

  verify(challengeId: string, code: string): Promise<SpinoffTwoFactorAuthVerified> {
    return firstValueFrom(
      this.http.post<SpinoffTwoFactorAuthVerified>(
        this.url(`/two-factor-auth/challenges/${encodeURIComponent(challengeId)}/verify`),
        { code },
        { context: localErrorContext() },
      ),
    );
  }

  /**
   * 사용자가 인증을 마칠 때까지 기다린다. 이미 진행 중이면 같은 프롬프트의 결과를 함께 기다린다.
   * 취소하면 `SpinoffTwoFactorAuthCancelledError`, 인증을 시작하지 못했으면(예: 번호 없음 409) 그 오류로 거부된다.
   */
  ensureVerified(allowedMethods: readonly SpinoffTwoFactorAuthMethod[]): Promise<void> {
    if (this.inflight) return this.inflight;
    const run = this.runPrompt(allowedMethods).finally(() => {
      this.inflight = null;
    });
    this.inflight = run;
    return run;
  }

  private async runPrompt(allowedMethods: readonly SpinoffTwoFactorAuthMethod[]): Promise<void> {
    const result = await this.prompt(allowedMethods);
    if (result.kind === 'verified') return;
    if (result.kind === 'cancelled') throw new SpinoffTwoFactorAuthCancelledError();
    throw result.error;
  }

  private url(path: string): string {
    return `${this.config.apiBaseUrl}${path}`;
  }
}

/** 호출마다 새로 만든다 — HttpContext는 가변 객체라 인스턴스를 공유하면 다른 요청의 설정이 섞인다. */
function localErrorContext(): HttpContext {
  return new HttpContext().set(SPINOFF_LOCAL_ERROR_HANDLING, true);
}
